import {AbstractParameter} from 'parameters/AbstractParameter.js';
import {RIGHT} from 'constants/hand-types.js';
import {EFFECT} from 'constants/object-types.js';
import {setPulledEffects} from 'actions/index.js';
import {MIDI_FX} from 'constants/effect-types.js';
import StepCacheable from './StepCacheable.js';
import AbstractEffect from 'effects/AbstractEffect.js';
import StepData from 'pattern/StepData.js';
import MidiTrigger from './MidiTrigger.js';
import LockableEffect from "effects/LockableEffect.js";

/**
 * Midi effects like "Arp" should be sub classes of this
 * in theory, should be able to link a series of midi effects
 * and have that effect the ultimate set of notes that are 
 * triggered by the sequencer
 */

class MidiEffect extends StepCacheable(LockableEffect(AbstractEffect)) {
    constructor(name, track, cacheSize) {
        super(name, track);
        if (this.track.sequencerEngine) {
            this.track.sequencerEngine.idToObject[this.id] = this;
        }
        this.triggers = [];
        this.initCache(cacheSize);

        this.numberOfOutputs = 1;
    }

    getObjectType() {
        return EFFECT;
    }
    
    getChordEffectType() {
        return null;
    }

    registerContext(context, parentDevice) {
        if (this.context) {
            this.context.combineWith(context);
        } else {
            this.context = context.getCopy();
        }
        if (parentDevice) {
            this.context.forSubDevice(this);
            this.context.forMidiEffect(parentDevice);
        } else {
            this.context.forMidiEffect(this);
        }
        this.parameters.forEach(
            parameter =>
                parameter.setContext(this.context));
    }

    getText() {
        let params = this.getVisualizedParameters().map(
            x => x.get(true))
            .map(x => typeof x === "number" ? Math.round(100*x)/100 : x)
            .map(x => x > 0 ? "+" + x : x);
        return params.join(", ");
    }

    getVisualizedParameters() {
        return this.parameters.filter(
            x => x.isMapped);
    }

    isRealTime() {
        return true;
    }

    getEffectType() {
        return MIDI_FX;
    } 

    isMidiEffect() {
        return true;
    }

    needsStableLookAhead() {
        return false;
    }
    
    onNewStep() {
        this.clear();
    }

    stop() {}
    
    clear() {
        if (this.triggers.length > 0) {
            this.triggers.length = 0;
        }
    }

    input(trigger) {
        trigger.outputNumber = 0;
        this.triggers.push(trigger);
    }

    // must be overridden by sub-classes
    output() {
        return this.triggers;
    }

    removeNote() {}

    getCopy(track) {
        return this;
    }

    addNote(transpose, sliceNumber=0, stepNumber=0) {
        let stepData = this.getStep(this.track.trackData, transpose, null, false);
        stepData.velocity = this.track.trackData.velocity;
        stepData.duration = null;
        stepData.transpose = transpose;
        stepData.slice = sliceNumber;

        this.input(new MidiTrigger(stepData, 0, stepNumber), true);
    }

    copyTrigger(trigger, useCache=false) {
        let duration = trigger.stepData.duration;
        let _stepData = trigger.stepData || new StepData();
        if (this.track && !this._subSequence) {
            _stepData.track = this.track;
        }
        let stepData = this.getStep(_stepData, undefined, undefined, useCache);
        let t = new MidiTrigger(stepData, trigger.time, trigger.stepNumber, trigger.outputNumber);
        if (trigger.grabs) {
            t.grabs = [... trigger.grabs];
        }
        if (trigger.chokes) {
            t.chokes = [... trigger.chokes];
        }
        if (trigger.message) {
            t.message = {... trigger.message};
        }
        if (trigger.delays) {
            t.message = {... trigger.delays};
        }
        return t;
    }

    dispose() {
        // we need to remove this from pulled effects if it exists
        this.name = undefined;
        let pulledEffects = this.track.sequencerEngine.state.effects.pulledEffects;
        this.track.sequencerEngine.dispatch(
            setPulledEffects(pulledEffects.filter(e => e !== this)));
    }

    getParameters() {
        return this.parameters;
    }

    isRolling() {
        return this.track.sequencerEngine.isSequenceRolling;
    }

}

export default MidiEffect;
